import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { PartySelect } from "@/components/catalog/PartySelect";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { listPartyOptions, syncPartiesFromApi, type PartyOption } from "@/lib/parties/partiesStore";
import {
  downloadPartyLedgerExcel,
  downloadPartyLedgerPdf,
  fetchPartyLedger,
  type PartyLedger,
  type PartyLedgerTypeFilter,
} from "@/lib/parties/partyLedgerStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { BookOpen, FileDown, FileSpreadsheet, Loader2, RotateCcw, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

function money(amount: number, symbol: string, emptyZero = false): string {
  if (emptyZero && Math.abs(amount) < 0.005) return "";
  const formatted = Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol ? `${symbol} ` : ""}${formatted}`;
}

function moneySide(amount: number, side: "Dr" | "Cr", symbol: string): string {
  return `${money(amount, symbol)} ${side}.`;
}

function formatDate(value: string | null): string {
  if (!value) return "";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

const TYPE_OPTIONS: { value: PartyLedgerTypeFilter; label: string }[] = [
  { value: "all", label: "All parties" },
  { value: "customer", label: "Customer" },
  { value: "supplier", label: "Supplier" },
  { value: "investor", label: "Investor" },
];

function partyMatchesType(party: PartyOption, type: PartyLedgerTypeFilter): boolean {
  if (type === "all") return true;
  const t = (party.type ?? "").toLowerCase();
  return t === type || t === "all" || t === "both";
}

const PartyLedgerPage = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView =
    can(user, "customer_can_view_any") ||
    can(user, "supplier_can_view_any") ||
    can(user, "investor_can_view_any") ||
    can(user, "chart_of_account_can_view_any");

  const [isForbidden, setIsForbidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [ledger, setLedger] = useState<PartyLedger | null>(null);
  const [parties, setParties] = useState<PartyOption[]>(() => listPartyOptions());

  const [typeFilter, setTypeFilter] = useState<PartyLedgerTypeFilter>("all");
  const [partyId, setPartyId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const currencySymbol = user?.company_branding?.currency?.symbol?.trim() || "";

  const activeFilters = useMemo(
    () => ({
      type: typeFilter,
      partyId: partyId ? Number(partyId) : null,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    }),
    [typeFilter, partyId, dateFrom, dateTo],
  );

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void syncPartiesFromApi()
      .then(() => {
        if (!cancelled) setParties(listPartyOptions());
      })
      .catch(() => {
        /* keep cached */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const partyOptions = useMemo(
    () => parties.filter((p) => partyMatchesType(p, typeFilter)),
    [parties, typeFilter],
  );

  const applyFilters = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setIsForbidden(false);
    try {
      const data = await fetchPartyLedger(activeFilters);
      setLedger(data);
      setHasApplied(true);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      console.error("[PartyLedger] Load failed", error);
      toast.error("Could not load party ledger.");
    } finally {
      setLoading(false);
    }
  }, [canView, activeFilters]);

  const exportReport = useCallback(
    async (format: "pdf" | "excel") => {
      if (!hasApplied || exporting) return;
      setExporting(format);
      try {
        if (format === "pdf") {
          await downloadPartyLedgerPdf(activeFilters);
          toast.success("PDF downloaded.");
        } else {
          await downloadPartyLedgerExcel(activeFilters);
          toast.success("Excel downloaded.");
        }
      } catch (error) {
        console.error(`[PartyLedger] ${format} export failed`, error);
        toast.error(format === "pdf" ? "Could not download PDF." : "Could not download Excel.");
      } finally {
        setExporting(null);
      }
    },
    [hasApplied, exporting, activeFilters],
  );

  const resetFilters = () => {
    setTypeFilter("all");
    setPartyId("");
    setDateFrom("");
    setDateTo("");
    setLedger(null);
    setHasApplied(false);
  };

  const titleType = TYPE_OPTIONS.find((o) => o.value === typeFilter)?.label ?? "All parties";
  const selectedPartyName = partyId
    ? partyOptions.find((p) => String(p.id) === String(partyId))?.name
    : null;

  const hasOpening = ledger != null && Math.abs(ledger.totals.openingBalance) >= 0.005;
  const hasRows = (ledger?.lines.length ?? 0) > 0 || hasOpening;

  if (!canView || isForbidden) {
    return (
      <Layout>
        <PermissionDenied />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <h1 className="font-display text-xl font-bold tracking-tight text-foreground">Party Ledger</h1>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Accounting sub-ledger by party type (customer, supplier, investor) from posted journal entries.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-border/55 bg-card/80 p-4 shadow-sm ring-1 ring-border/25">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Party type</Label>
              <Select
                value={typeFilter}
                onValueChange={(v) => {
                  setTypeFilter(v as PartyLedgerTypeFilter);
                  setPartyId("");
                }}
              >
                <SelectTrigger className="glass-input h-9 text-xs">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 sm:col-span-1 lg:col-span-2">
              <Label className="text-xs font-semibold text-foreground">Party</Label>
              <PartySelect
                value={partyId}
                options={partyOptions}
                onChange={setPartyId}
                placeholder={`All ${typeFilter === "all" ? "parties" : `${typeFilter}s`}`}
                clearable
                clearLabel={`All ${typeFilter === "all" ? "parties" : `${typeFilter}s`}`}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">From date</Label>
              <DatePartsPicker
                value={dateFrom}
                onChange={setDateFrom}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                maxDate={dateTo ? new Date(`${dateTo}T00:00:00`) : undefined}
                placeholder="Select from date"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">To date</Label>
              <DatePartsPicker
                value={dateTo}
                onChange={setDateTo}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                minDate={dateFrom ? new Date(`${dateFrom}T00:00:00`) : undefined}
                placeholder="Select to date"
                align="end"
              />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {hasApplied ? (
                <>
                  Showing: <span className="font-semibold text-foreground">{titleType}</span>
                  {selectedPartyName ? (
                    <>
                      {" · "}
                      <span className="font-semibold text-foreground">{selectedPartyName}</span>
                    </>
                  ) : null}
                </>
              ) : (
                "Choose filters, then click Apply filter."
              )}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={!hasApplied || loading || exporting != null}
                onClick={() => void exportReport("pdf")}
              >
                {exporting === "pdf" ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileDown className="mr-1.5 h-3.5 w-3.5" />
                )}
                PDF
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={!hasApplied || loading || exporting != null}
                onClick={() => void exportReport("excel")}
              >
                {exporting === "excel" ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" />
                )}
                Excel
              </Button>
              <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={resetFilters}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Reset
              </Button>
              <Button
                type="button"
                variant="premium"
                size="sm"
                className="h-8 text-xs"
                onClick={() => void applyFilters()}
                disabled={loading}
              >
                {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Search className="mr-1.5 h-3.5 w-3.5" />}
                Apply filter
              </Button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-border/55 bg-card/80 shadow-sm ring-1 ring-border/25">
          {loading ? (
            <div className="flex h-48 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : !hasApplied ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No ledger loaded yet. Apply a filter to view entries.
            </p>
          ) : !ledger || !hasRows ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No posted ledger entries for this filter.
            </p>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date / Voucher #</TableHead>
                    <TableHead>Particulars</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-muted/30">
                    <TableCell className="font-semibold text-foreground" colSpan={2}>
                      Opening Balance
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground" />
                    <TableCell className="text-right tabular-nums text-muted-foreground" />
                    <TableCell className="text-right tabular-nums font-semibold">
                      {moneySide(ledger.totals.openingBalance, ledger.totals.openingBalanceSide, currencySymbol)}
                    </TableCell>
                  </TableRow>
                  {ledger.lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell className="min-w-[8rem] whitespace-nowrap">
                        <div className="text-sm text-foreground">{formatDate(line.date)}</div>
                        <div className="font-mono text-[11px] text-muted-foreground">{line.voucher || ""}</div>
                      </TableCell>
                      <TableCell className="min-w-[10rem] max-w-[18rem]">
                        <div className="space-y-0.5">
                          {line.particulars.map((text, idx) => (
                            <p
                              key={`${line.id}-p-${idx}`}
                              className={cn(
                                "leading-snug",
                                idx === 0
                                  ? "text-xs font-medium text-foreground"
                                  : "text-[11px] text-muted-foreground",
                              )}
                            >
                              {text}
                            </p>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                        {money(line.debit, currencySymbol, true)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-rose-700 dark:text-rose-400">
                        {money(line.credit, currencySymbol, true)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {moneySide(line.balance, line.balanceSide, currencySymbol)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={2} className="font-semibold">
                      Closing Balance
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-emerald-700 dark:text-emerald-400">
                      {money(ledger.totals.debit, currencySymbol)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-rose-700 dark:text-rose-400">
                      {money(ledger.totals.credit, currencySymbol)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right tabular-nums font-semibold",
                        ledger.totals.balance < 0 && "text-rose-700",
                      )}
                    >
                      {moneySide(ledger.totals.balance, ledger.totals.balanceSide, currencySymbol)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PartyLedgerPage;
